/**
 * Which time slots a Stationary assignment has to fill, and what a legal one looks like.
 *
 * A Stationary plan books the trainer's own diary, so every generated occurrence needs a time of
 * day. The backend takes that as a `DayOfWeek → "HH:mm"` map, at most eight entries, and resolves
 * each occurrence in this exact order (`ScheduleGenerator.ApplyStationaryTime`):
 *
 *   1. the entry whose key equals the DAY TEMPLATE's own `DayOfWeek`;
 *   2. failing that, the entry under key **0** — the documented fallback;
 *   3. failing that, **whichever entry the dictionary happens to yield first**;
 *   4. failing that, midnight.
 *
 * Steps 3 and 4 are why the slot set is derived here rather than guessed at the input. The keys the
 * trainer is asked to fill are exactly the keys that resolution reads, so no occurrence can land on
 * an arbitrary time or on 00:00.
 *
 * ── The key-0 slot, and the mobile defect it closes ──────────────────────────────────────────
 * A DateBased day may legally declare NO weekday: the schedule generator then places it on the
 * start date's weekday (the fixture pins that as
 * `date-based-day-without-weekday-uses-start-weekday`). The TIME resolution above does **not** do
 * the same thing — it reads the template's raw, still-null `DayOfWeek`, misses every weekday key,
 * and falls through to step 2 or 3.
 *
 * Mobile's `defaultWeekdayTimes` seeds a key only `if (day.dayOfWeek != null)`, so such a day
 * reaches step 3 there and is booked at whatever time the dictionary iterates first. This app offers
 * a key-0 slot whenever the plan carries a day with no declared weekday, which is what step 2 exists
 * for. Raised for the mobile app as its own fix rather than replicated.
 *
 * Key 0 used to be described as mobile's reservation for sequence plans. That is no longer a
 * meaningful statement — `SequenceBased` is gone from the wire, and the mode that replaced it never
 * reaches this module at all, because R11 makes a self-paced assignment remote-only and there is no
 * Stationary fork to collect hours for. Every plan that gets here is DateBased, and key 0 means what
 * the paragraph above says it means: the day this plan did not give a weekday to.
 *
 * The eight-entry ceiling still holds by construction: seven weekdays plus the fallback is eight.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type { ScheduleTree } from './assignSchedule';

/**
 * The backend's fallback key — `StationaryTimeInput.DayOfWeek` is `Range(0, 7)` precisely so this
 * value can be sent. It is NOT a weekday.
 */
export const WEEKDAY_FALLBACK_KEY = 0;

/** The time a slot starts at before the trainer touches it. Mobile's own default. */
export const DEFAULT_SESSION_TIME = '17:00';

/** `HH:mm`, 24-hour — the pattern the wire declares on `OccurrenceDateOverrideInput.time`. */
const TIME_OF_DAY = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Whether a string is a time the backend will parse. */
export function isTimeOfDay(value: string): boolean {
  return TIME_OF_DAY.test(value);
}

/** A weekday key is meaningful only when it is a real ISO weekday. */
function isIsoWeekday(value: number | null): value is number {
  return value !== null && Number.isInteger(value) && value >= 1 && value <= 7;
}

/**
 * Every key a Stationary assignment of this plan must carry a time for, ascending.
 *
 * `WEEKDAY_FALLBACK_KEY` sorts first, which is also how it reads: the general time, then the
 * per-weekday exceptions.
 */
export function stationaryTimeSlots(tree: ScheduleTree): number[] {
  const keys = new Set<number>();
  let needsFallback = false;

  for (const phase of tree.phases) {
    for (const day of phase.days) {
      if (day.sessions.length === 0) continue;
      if (isIsoWeekday(day.dayOfWeek)) keys.add(day.dayOfWeek);
      // A day with no declared weekday resolves its TIME through key 0 — see the header.
      else needsFallback = true;
    }
  }

  if (needsFallback) keys.add(WEEKDAY_FALLBACK_KEY);
  return [...keys].sort((a, b) => a - b);
}

/** Every slot seeded at {@link DEFAULT_SESSION_TIME}. */
export function defaultStationaryTimes(tree: ScheduleTree): Record<number, string> {
  const times: Record<number, string> = {};
  for (const key of stationaryTimeSlots(tree)) times[key] = DEFAULT_SESSION_TIME;
  return times;
}
